import {
  type FleetDTO,
  fetchFleet as fetchFleetData,
} from "@/api/vtnz-backend/fleet";
import type { FetchState } from "@/types/sharedTypes";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

export const useFleetStore = defineStore("fleetStore", () => {
  // ---------------------------------- State --------------------------------- //

  const fleetRawData = ref<FleetDTO | null>(null);

  const fetchState = ref<FetchState>("idle");
  const errorMsg = ref<string | null>(null);

  // --------------------------------- Getters -------------------------------- //

  const fleetId = computed<string | null>(
    () => fleetRawData.value?.id ?? null,
  );

  const fleetName = computed<string | null>(
    () => fleetRawData.value?.name ?? null,
  );

  const logoSrc = computed<string>(
    () => fleetRawData.value?.logo_url ?? '/images/VTNZ-Test-logo.jpg',
  );

  const hasFleetData = computed<boolean>(() => fleetRawData.value !== null);

  const isFetching = computed(() => fetchState.value === "loading");

  // --------------------------------- Actions -------------------------------- //

  async function fetchFleet() {
    fetchState.value = "loading";

    const fetchRes = await fetchFleetData();

    if (!fetchRes.success) {
      errorMsg.value = fetchRes.errorMsg;
      fetchState.value = "error";
      return;
    }

    fleetRawData.value = fetchRes.data;

    fetchState.value = "success";
  }

  function $reset() {
    fetchState.value = "idle";
    fleetRawData.value = null;
  }

  return {
    // --------------------------------- State -------------------------------- //

    /** indicates the fleet store's current data fetching state */
    fetchState,

    /** error message for last fetching error if there is one. */
    errorMsg,

    // -------------------------------- Getters ------------------------------- //

    /** helper flag to indicated async fetching is in operation. Good for loading animations. */
    isFetching,

    /** indicates whether the fleet store has data available */
    hasFleetData,

    /** Image source for the fleet logo */
    logoSrc,

    /** Database id of the current fleet. */
    fleetId,

    fleetName,
    // -------------------------------- Actions ------------------------------- //

    /** fetch the fleets data from the backend and apply it to store state. */
    fetchFleet,

    /** Resets the store to its initial state. */
    $reset,
  };
});
